import { defaultLanguage } from '../../../Intl/setup.js';

import { formatPrice, getPriceCent, getPriceFromCent } from '../../util/price.js';

import { getProductPrice } from '../Product/ProductActions.js';
import { getUserOption } from '../User/UserActions.js';
import { getTranslation } from '../Intl/IntlHelper.js';
import { getRandomString, slugify } from '../../util/text.js';

export const SET_ITEM = 'SET_ITEM';
export const SET_ITEM_QUANTITY = 'SET_ITEM_QUANTITY';
export const REMOVE_ITEM = 'REMOVE_ITEM';
export const REMOVE_ITEMS = 'REMOVE_ITEMS';
export const SET_INVOICE_ADDRESS = 'SET_INVOICE_ADDRESS';
export const SET_SHIPPING_ADDRESS = 'SET_SHIPPING_ADDRESS';
export const SET_SHIPPING_METHOD = 'SET_SHIPPING_METHOD';
export const SET_CART_OPTION = 'SET_CART_OPTION';
export const ADD_CART_NOTE = 'ADD_CART_NOTE';
export const CLEAR_CART = 'CLEAR_CART';

// Getters
export function getCart(store) {
    return store.cart;
}

export function getCartItems(store) {
    return store.cart.items;
}

export function getCartItemByProduct(store, product) {
    return store.cart.items.find(item => item.productId === product._id);
}

export function getCartNote(store) {
    return store.cart.note;
}

export function getCartShipping(store) {
    return store.cart.shipping;
}

export function getCartInvoice(store) {
    return store.cart.invoice;
}

export function getCartAddress(store, type) {
    return (store.cart[type] || {}).address;
}

export function getCartVouchers(store) {
    return store.cart.vouchers;
}

export function getCartOptions(store) {
    return store.cart.options || {};
}

export function getCartItemFromProduct(product, quantity = 1, locale = defaultLanguage, extra = {}) {
    if(product) {
        const { _id, __v, priceTTC, priceHT, weightNet, description, translations, pictures, annexes, isHighlight, isActive, ...productData } = product;
		// console.log('Cart::: getCartItemFromProduct:', product, quantity, getProductPrice(product, quantity, true, extra.referral), getProductPrice(product, quantity, true, extra.referral) / parseInt(quantity || 1, 10));
        return {
			...productData,
			id: getRandomString(4),
            productId: product._id || product.productId, // product or item
            quantity: parseInt(quantity, 10),
			priceTTC,
			priceHT,
            basePriceTTC: priceTTC,
            basePriceHT: priceHT,
            // Base euro à laquelle correspondent currencyPrices (prix unitaire, avant remise de ligne).
            catalogBaseTTC: (extra && extra.catalogBaseTTC != null) ? extra.catalogBaseTTC : priceTTC,
            status: 'ordered',
            name: getTranslation(product, 'name', locale),
            nameAdmin: product.name,
            ...(extra || {}),
        };
    }
	return null;
}

export function getCartQuantityStep(product) {
    return Math.max(product.stepQuantity || 1, 1);
}

export function getPackages(items) {
    const packages = [];
    (items || []).forEach(item => {
        if(item.packing && item.quantity > item.packing) {
            const threshold = Math.ceil(item.quantity / item.packing);
            for(let i = 0; i < threshold; i += 1) {
                packages.push({
                    items: [{
                        ...item,
                        quantity: (i + 1) !== threshold ? item.packing : item.quantity - (i * item.packing),
                    }],
                });
            }
        } else {
            packages.push({ items: [{ ...item }] });
        }
    });
    // console.log('Order packages', packages);
    return packages;
}

export function getParcels(method, items) {
    if(!method.parcelGroup) {
        // console.log('getParcels ::: Method without parcel grouping');
        return [[{ items }]];
    }

    const parcels = [];
    const orderPackages = getPackages(items);

    for(let i = 0; i < Math.ceil(orderPackages.length / method.parcelGroup); i += 1) {
        parcels.push([...orderPackages.slice(i * method.parcelGroup, (i + 1) * method.parcelGroup)]);
    }

    // console.log('orderParcels', parcels);
    return parcels;
}

/**
 * Poids papier + carton approximatif : 1,25 g par page générée
 * (inclut une part d'emballage carton).
 * Ex. 400 pages → 500 g → 0,50 kg.
 */
export const BOOK_PAGE_WEIGHT_GRAMS = 1.25;

/**
 * Ligne 100 % numérique : produit ebook seul, sans livre papier associé.
 * Ces lignes ne sont jamais expédiées : ni poids, ni pages, quel que soit
 * le poids saisi sur le produit ebook en base.
 */
export function isDigitalOnlyItem(item) {
	const options = (item || {}).options || {};
	return !!options.useForConversationEBook && !options.useForConversationBook;
}

/**
 * Panier / commande 100 % numérique : au moins une ligne livre, et toutes les
 * lignes livre sont des ebooks. Source unique de vérité partagée par le serveur
 * (auto-clôture des commandes ebook) et l'outil imprimeur.
 */
export function isEbookOnlyItems(items) {
	const bookItems = (items || []).filter(item => (
		item
		&& item.status === 'ordered'
		&& (item.options?.useForConversationBook || item.options?.useForConversationEBook)
	));

	return bookItems.length > 0 && bookItems.every(isDigitalOnlyItem);
}

/**
 * Poids d'expédition d'une ligne (kg).
 * Priorité : pagesCount × 1,25 g × quantité (livre papier).
 * Repli : poids produit unitaire × quantité.
 * Ebook / poids 0 : 0.
 */
export function getItemShippingWeightKg(item, products = null) {
	if(!item || item.status !== 'ordered') {
		return 0;
	}
	const quantity = parseInt(item.quantity, 10) || 0;
	if(quantity <= 0) {
		return 0;
	}

	if(isDigitalOnlyItem(item)) {
		return 0;
	}

	const product = false && products && products.length
		? products.find(candidate => candidate._id === item.productId)
		: null;
	const unitProductWeight = parseFloat(product && !item.forceUseItemData ? product.weight : item.weight) || 0;

	// Pas de port (ebook / crédit) : ne pas appliquer le calcul pages.
	if(!(unitProductWeight > 0)) {
		return 0;
	}

	const pagesCount = parseInt(item.pagesCount, 10);
	if(!Number.isNaN(pagesCount) && pagesCount > 0) {
		return formatPrice((pagesCount * BOOK_PAGE_WEIGHT_GRAMS * quantity) / 1000);
	}

	return formatPrice(quantity * unitProductWeight);
}

/**
 * Poids total d'expédition (kg) : somme des lignes (pages × 1,25 g si dispo).
 */
export function getTotalWeight(items, products = null) {
	return getPriceFromCent((items || []).reduce((total, item) => (
		total + getPriceCent(getItemShippingWeightKg(item, products))
	), 0));
}

/**
 * Panier / commande à expédier : au moins une ligne physique. On se base sur le poids
 * d'expédition et non sur le poids brut du produit, sinon un poids résiduel saisi sur
 * le produit ebook ferait basculer une commande numérique dans le parcours papier.
 */
export function needsShipping(items, products = null) {
	return (items || []).some(item => getItemShippingWeightKg(item, products) > 0);
}

/**
 * Total de pages générées pour une conversation, tomes papier uniquement :
 * un tome PDF n'est jamais imprimé ni expédié, il ne doit pas peser.
 */
export function getConversationPagesCount(conversation) {
	return (conversation?.books || []).reduce((total, book) => (
		book?.type === 'pdf' ? total : total + (parseInt(book?.pagesCount, 10) || 0)
	), 0);
}

/**
 * Complète pagesCount sur les lignes panier / commande depuis les conversations
 * (utile si l'item a été ajouté avant le calcul pages, ou rechargé sans ce champ).
 */
export function enrichItemsWithPagesCount(items, conversationsById = {}) {
	return (items || []).map(item => {
		if(!item || item.pagesCount > 0 || !item.conversationId || isDigitalOnlyItem(item)) {
			return item;
		}
		const conversation = conversationsById[item.conversationId]
			|| conversationsById[`${item.conversationId}`];
		const pagesCount = getConversationPagesCount(conversation);
		if(!(pagesCount > 0)) {
			return item;
		}
		return { ...item, pagesCount };
	});
}

export function getTotalQuantity(items, ignoreStatus = false) {
    return getPriceFromCent((items || []).filter(item => ignoreStatus || item.status === 'ordered').reduce((quantity, item) => quantity + getPriceCent((item && item.quantity * ((item.items || []).length ? item.items.reduce((packItemTotal, packItem) => packItemTotal + packItem.quantity, 0) : 1)) || 0), 0));
}

export function getSubtotal(items, products = null, includingTax = true, referral = null) {
    return getPriceFromCent(getPriceCent((items || []).filter(item => item.status === 'ordered').reduce((total, item) => {
        const priceKey = includingTax ? 'priceTTC' : 'priceHT';
        const product = false && products && products.length ? products.find(product => product._id === item.productId) : null;
        return total
			+ getProductPrice(item, item.quantity, includingTax, referral);
			// + (item.quantity * (product && !item.forceUseItemData ? product[priceKey] : item[priceKey]) || 0);
    }, 0)));
}

export function getTotalTax(items, products = null, taxRate = null, user = null, referral = null) {
    if(items && items.length && !getUserOption(user, 'exemptVAT')) {
        return formatPrice(
            items
                .filter(item => item.status === 'ordered')
                .map(item => ({
                    ...item,
                    ...((false && !item.forceUseItemData && products && products.length && products.find(product => product._id === item.productId)) || {}),
                }))
                .filter(item => parseFloat(item.taxRate) === parseFloat(taxRate) || taxRate === null)
                .reduce((subtotal, item) => {
                    const lineTotalTTC = getProductPrice(item, item.quantity, true, referral);
                    const lineTotalHT = lineTotalTTC / (1 + parseFloat(item.taxRate));
                    const lineTotalTax = lineTotalTTC - lineTotalHT;
                    return subtotal + lineTotalTax;
                }, 0),
        );
    }
    items && items.length && console.error('TAX set to 0!', items, products, taxRate, getUserOption(user, 'exemptVAT') && 'TVA exempted');
    return 0;
}

export function isConditionPercentValue(condition) {
    return ((condition || {}).value || '').indexOf('%') !== -1;
}

export function getConditionValue(condition) {
    let conditionValue = parseFloat(condition.value);
    if(isConditionPercentValue(condition)) {
        conditionValue = parseFloat(condition.value.replace('%', ''));
    }
    return conditionValue;
}

export function getConditionValueToCheck(condition, items, referral = null) {
    let valueToCheck = 0;

   if(condition.key === 'weight') { // check order total weight for all suborderitems
       valueToCheck = getTotalWeight(items);
   } else if(condition.key === 'subtotal') { // check order sub total for all suborderitems
       valueToCheck = getSubtotal(items, null, true, referral);
   } else if(condition.key === 'packCount') {
       valueToCheck = getPackages(items).length;
   } else {
       console.error('ConditionKeyMissing');
   }
   return valueToCheck;
}

export function isConditionChecked(condition, items, referral = null, exceptions = []) {
    // console.log('isConditionChecked ::: Start', condition, items, exceptions);

    if(exceptions && exceptions.includes(condition.key)) {
        // console.log('isConditionChecked ::: Allowed by exceptions');
        return true;
    }

    const conditionValue = getConditionValue(condition);
    const valueToCheck = getConditionValueToCheck(condition, items, referral);

    // console.log('isConditionChecked ::: Check', condition.key, `${valueToCheck}${isConditionPercentValue(condition) ? '%' : ''} ${condition.limit === 'min' ? '>' : '<'}${condition.tolerance === 'exclusive' ? '' : '='} ${conditionValue}${isConditionPercentValue(condition) ? '%' : ''}`);
    if(condition.limit === 'min') {
        if(condition.tolerance === 'exclusive') {
            return valueToCheck > conditionValue;
        }
        // inclusive
        return valueToCheck >= conditionValue;
    }
    // max
    if(condition.tolerance === 'exclusive') {
        return valueToCheck < conditionValue;
    }
    // inclusive
    return valueToCheck <= conditionValue;
}

export function getConditionCheckPercentage(condition, items, referral = null) {
    return (getConditionValueToCheck(condition, items, referral) * 100) / getConditionValue(condition);
}

export function formatAnalyticsItems(items) {
    const getCategories = item => {
        const categories = {};
        (item.categories || []).forEach((category, index) => {
            let categoryFieldName = 'item_category';
            if(index > 0) {
                categoryFieldName += index;
            }
            categories[categoryFieldName] = category;
        });
        return categories;
    };
    return (items || []).map(item => ({
        item_id: item.slug || slugify(item.name),
        item_name: item.name,
        price: formatPrice(parseFloat(item.priceTTC)),
        quantity: parseInt(item.quantity, 10),
        item_brand: 'MLSMS',
        ...getCategories(item),
    }));
}

export function getAvailableConditionKeys() {
    return ['weight', 'subtotal', 'packCount'];
}

// Actions
export function setCartItem(item) {
    return {
        type: SET_ITEM,
        item,
    };
}

export function setCartItemQuantity(itemId, quantity) {
    return {
        type: SET_ITEM_QUANTITY,
        itemId,
		quantity,
    };
}

export function removeItem(itemId) {
    return {
        type: REMOVE_ITEM,
        itemId,
    };
}

export function removeItems() {
    return {
        type: REMOVE_ITEMS,
    };
}

export function clearCart() {
    return {
        type: CLEAR_CART,
    };
}

export function setShippingAddress(address = {}) {
    return {
        type: SET_SHIPPING_ADDRESS,
        address,
    };
}

export function setShippingMethod(method = {}) {
    return {
        type: SET_SHIPPING_METHOD,
        method,
    };
}

export function setInvoiceAddress(address = {}) {
    return {
        type: SET_INVOICE_ADDRESS,
        address,
    };
}

export function setCartOption(key, value) {
    return {
        type: SET_CART_OPTION,
        key,
        value,
    };
}

export function setCartNote(note) {
    return {
        type: ADD_CART_NOTE,
        note,
    };
}
